import React, { useCallback, useMemo } from 'react';
import { changePageTitle } from '@ubeya/shared/hooks/usePageTitle';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import styled from 'styled-components';
import { ReactComponent as SoonChip } from '@ubeya/shared-web/assets/soon-tag.svg';
import { ModuleIcons } from '@ubeya/shared-web/assets/SidebarIcons';
import { ReactComponent as BaseStarIcon } from '@ubeya/shared-web/assets/star-is-host.svg';
import { ReactComponent as BaseArrowRightIcon } from '@ubeya/shared-web/assets/arrow-right-small.svg';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { FlexCenter, FlexMiddle } from '../Flex';
import { Body, Popover } from '../index';

const StarIcon = styled(BaseStarIcon)`
  position: absolute;
  top: -2px;
  right: -2px;
`;

const Submenu = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
`;

const SubmenuContainer = styled.div`
  padding: 8px;
  padding-top: ${({ $isExpanded }) => (!$isExpanded ? '0' : '8px')};
`;

const CircleIcon = styled(FlexCenter)`
  border-radius: 8px;
  cursor: pointer;
  transition: 200ms;
  width: 40px;
  height: 40px;
  min-width: 40px;
  position: relative;
`;

const SubmenuRow = styled.div`
  text-decoration: none;
  display: flex;
  background-color: ${({ theme }) => theme.colors.white};
  align-items: center;
  justify-content: flex-start;
  padding: 8px 8px 8px 16px;
  gap: 8px;
  border-radius: 4px;
  cursor: ${({ $disabled }) => ($disabled ? 'normal' : 'pointer')};
  transition: all;
  color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.primary : theme.colors.black)};
  :hover {
    background-color: ${({ theme, $disabled }) => !$disabled && theme.colors.hoverBackground};
  }
`;

const BottomBorder = styled.div`
  height: 1px;
  width: 100%;
  margin-top: 10px;
  background-color: ${({ theme }) => theme.colors.white}99;
`;

const PopoverWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const SubmenuText = styled.div`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const NewModuleIcon = styled(ModuleIcons.CommonModuleIcons.NewModule)`
  position: absolute;
  top: 3px;
  right: 3px;
`;

const Link = styled(FlexMiddle)`
  text-decoration: none;
  width: 100%;
  position: relative;
  padding-right: ${({ $isExpanded }) => ($isExpanded ? '8px' : '0')};
  justify-content: ${({ $isExpanded }) => ($isExpanded ? 'space-between' : 'center')};
  flex-direction: ${({ $isExpanded }) => ($isExpanded ? 'row' : 'column')};
  border-radius: 8px;
  ${({ $mainIcon }) => $mainIcon && 'margin-bottom: 44px;'}
  background-color: ${({ theme, $isExpanded, $shouldHover, $shouldBeSelected }) => {
    if (!$isExpanded) return 'transparent';
    if ($shouldBeSelected) return theme.colors.primary;
    if ($shouldHover) return `${theme.colors.white}26`;
    return 'transparent';
  }};

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.white}26`};
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};

    ${StarIcon} {
      opacity: 1;
    }
  }

  ${StarIcon} {
    opacity: 0;
  }

  &:hover {
    ${StarIcon} {
      opacity: 1;
    }
  }
`;

const isSecondaryItemActive = (pathname, path) => {
  if (!pathname || !path) return false;
  const pathnameSegments = pathname.split('/').filter(Boolean);
  const pathFirstSegment = path.replace(/^\//, '').split('/')[0];
  const firstSegmentAfterAccount = pathnameSegments[1];
  return pathname.includes(path) && firstSegmentAfterAccount === pathFirstSegment;
};

const PopoverContainer = React.forwardRef((props, ref) => (
  <PopoverWrapper ref={ref} onClick={props.onClick ? props.onClick : () => {}}>
    {props.children}
  </PopoverWrapper>
));

const Label = styled(Body)`
  transition: 200ms;
  font-size: 14rem;
  display: flex;
  align-items: center;
  padding: 8px 8px ${({ hasMenu }) => (hasMenu ? 4 : 8)}px 12px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  text-decoration: none;
`;

const TriggerContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const TriggerLabel = styled(Body)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 14rem;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: ${({ $shouldWrap }) => ($shouldWrap ? 'normal' : 'nowrap')};
  text-overflow: clip;
`;

const ArrowRightIcon = styled(BaseArrowRightIcon)`
  flex-shrink: 0;
  transform: ${({ $isRtl }) => ($isRtl ? 'rotate(180deg)' : 'none')};

  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

export const AppSideBarItem = React.memo(
  ({
    to,
    Icon,
    mainIcon,
    t,
    label,
    mobileLabel,
    isOpen,
    disabled,
    hasBottomBorder,
    LinkRenderer,
    isRtl,
    pathname,
    onHover,
    openPopoverIndex,
    isHost,
    index,
    accountName,
    includePath = [],
    isNew,
    secondaryMenu,
    shouldChangePageTitle = true,
    accessory: Accessory,
    isExpanded = false,
    isAnimationFinished = false,
    onCollapseSidebar
  }) => {
    const resolvedLabel = label || mobileLabel || '';
    const from = (pathname || '').split('/')?.[2];
    const { accountId } = useAccount();
    const { data: { id: adminId } = {} } = useUser();

    const onClick = useCallback(() => {
      if (shouldChangePageTitle) {
        changePageTitle(accountName, resolvedLabel, t);
      }

      trackEvent('Module Sidebar Tab Clicked', {
        context: 'Module Sidebar',
        prevTab: t(from),
        newTab: t(resolvedLabel)
      });
      if (isExpanded) {
        onCollapseSidebar?.();
      }
    }, [shouldChangePageTitle, t, from, resolvedLabel, accountName, isExpanded, onCollapseSidebar]);

    const splitPathname = (pathname || '').split('/').filter(Boolean);
    const filteredSecondaryMenu = (secondaryMenu || []).filter((sm) => !sm.isHidden);
    const hasSubMenu = filteredSecondaryMenu.length > 0;
    const shouldShowPopover = !mainIcon && (!isExpanded || hasSubMenu);

    const shouldBeSelected = useMemo(
      () =>
        (filteredSecondaryMenu || [])
          .map((secondaryMenuItem) => secondaryMenuItem.to.split('/:accountId/')?.[1])
          .some((subpath) => (subpath || '') === splitPathname) || includePath.includes(splitPathname),
      [filteredSecondaryMenu, includePath, splitPathname]
    );

    const handleCircleIconMouseLeave = useCallback(
      (e) => {
        //Position where mouse left the container on the X axis
        const ox = e.nativeEvent.offsetX;
        //Position where mouse left the container on the Y axis
        const oy = e.nativeEvent.offsetY;
        if ((isRtl && (ox > 5 || oy < 6)) || (!isRtl && (ox < 24 || oy < 1))) {
          onHover(-1);
        }
      },
      [isRtl, onHover]
    );

    const handleSubmenuMouseLeave = useCallback(
      (e) => {
        const ox = e.nativeEvent.offsetX;
        const oy = e.nativeEvent.offsetY;
        if ((!isRtl && (oy < 1 || oy > 31 || ox > 10)) || (isRtl && (ox < 195 || (ox > 195 && oy > 30)))) {
          onHover(-1);
        }
      },
      [isRtl, onHover]
    );

    return (
      <Popover
        isPopoverOpen={index === openPopoverIndex && shouldShowPopover}
        onClickOutside={() => onHover(-1)}
        popoverContentStyle={{ borderRadius: 8, zIndex: 10000, minWidth: 200 }}
        containerStyle={{
          zIndex: 4,
          top: '-2px'
        }}
        arrowStyle={{
          marginTop: 3
        }}
        popoverContent={
          <Submenu onMouseLeave={handleSubmenuMouseLeave}>
            {!isExpanded && (
              <Label as={LinkRenderer} to={to} hasMenu={hasSubMenu}>
                {t(resolvedLabel)}
              </Label>
            )}

            {hasSubMenu && (
              <SubmenuContainer $isExpanded={isExpanded}>
                {(filteredSecondaryMenu || []).map((menuItem) => {
                  const path = menuItem.to.split('/:accountId')[1];
                  const isActive = isSecondaryItemActive(pathname, path);
                  return (
                    <SubmenuRow
                      as={menuItem.onClick || menuItem.disabled ? undefined : LinkRenderer}
                      key={menuItem.label + menuItem.to}
                      to={menuItem.to}
                      $disabled={menuItem.disabled}
                      $isHost={isHost && disabled}
                      onClick={() => {
                        if (menuItem.onClick) {
                          menuItem.onClick();
                        }
                        if (menuItem.label === 'help_desk') {
                          trackEvent('Help Desk - Admin Opened Module', {
                            module: 'Help Desk',
                            context: 'navigated to Help Desk from left panel',
                            referral: 'Left panel',
                            adminId,
                            accountId
                          });
                        }
                        trackEvent('Module Sidebar Secondary Menu Tab Clicked', {
                          context: 'Module Sidebar Secondary Menu',
                          parentTab: t(resolvedLabel),
                          prevTab: t(from),
                          newTab: t(menuItem.label)
                        });
                        if (isExpanded) {
                          onCollapseSidebar?.();
                        }
                      }}
                      $isSelected={isActive}>
                      <SubmenuText $disabled={menuItem.disabled}>{t(menuItem.label || '')}</SubmenuText>
                      {menuItem.disabled && <SoonChip />}
                    </SubmenuRow>
                  );
                })}
              </SubmenuContainer>
            )}
          </Submenu>
        }
        triggerContent={
          <PopoverContainer>
            <Link
              as={LinkRenderer}
              to={to}
              $shouldHover={index === openPopoverIndex || (!!Accessory && mainIcon)}
              $shouldBeSelected={shouldBeSelected}
              $isOpen={isOpen}
              $isHost={isHost}
              $isExpanded={isExpanded}
              onClick={onClick}
              $mainIcon={mainIcon}
              onMouseEnter={() => onHover(shouldShowPopover ? index : -1)}
              data-testid={`sidebar-item-${label}`}>
              <TriggerContent $isExpanded={isExpanded}>
                <CircleIcon
                  $disabled={disabled}
                  $isExpanded={isExpanded}
                  onMouseLeave={handleCircleIconMouseLeave}
                  onMouseEnter={() => onHover(shouldShowPopover ? index : -1)}>
                  <Icon />
                  {isNew && <NewModuleIcon />}
                  {!!Accessory && <Accessory />}
                  {isHost && disabled && <StarIcon />}
                </CircleIcon>

                {isExpanded && !!resolvedLabel && (
                  <TriggerLabel $shouldWrap={isAnimationFinished} $disabled={disabled}>
                    {t(resolvedLabel)}
                  </TriggerLabel>
                )}
                {isExpanded && hasSubMenu && <ArrowRightIcon $disabled={disabled} $isRtl={isRtl} />}
              </TriggerContent>
            </Link>
            {hasBottomBorder && <BottomBorder />}
          </PopoverContainer>
        }
      />
    );
  }
);
